import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { withTranslation } from 'language/i18n';
import { PaymentContext } from 'contexts/paymentContext';
import { paymentConsts } from 'constants/payment';
import maskAmount from 'utils/maskAmount';
import Breadcrumb from 'components/Breadcrumb';
import Layout from 'components/partials/Layout';
import { turkishNumberWithSuffix } from 'utils/numberFormatters';
import LeftMenu from '../components/buying/LeftMenu';
import BeforeBiddingPartial from '../components/buying/BeforeBiddingPartial';

const BeforeBidding = ({ t }) => {
  const { paymentData } = useContext(PaymentContext);

  const breadcrumbs = [
    {
      label: t('homepage'),
      url: '/',
    },
    {
      label: t('buying'),
      url: '/content/buying/before-bidding',
    },
    {
      label: 'Teklif Vermeden Önce Okunması Gerekenler',
      url: '/content/buying/before-bidding',
    },
  ];

  return (
    <main className="has-header">
      <div className="container">
        <div className="row mt-4 mb-4 mt-lg-3 mb-lg-3">
          <div className="col">
            <Breadcrumb links={breadcrumbs} />
          </div>
        </div>
        <div className="row">
          <LeftMenu currentPage="before-bidding" />
          <div className="col-md-10 col-lg-11 mt-3 mt-md-0 before-bidding-container">
            <div className="row mb-20">
              <div className="col">
                <h1 className="h1">Teklif Vermeden Önce Okunması Gerekenler</h1>
              </div>
            </div>
            <div className="row mb-20">
              <div className="col-15">
                <h2 className="content-header">Ücretsiz Üyelik, Ücretsiz Teklif</h2>
              </div>
              <div className="col-15">
                <p>
                  iyiMakina.com’a ücretsiz üye olabilir, istediğiniz makinaya istediğiniz teklifi ücretsiz olarak verebilirsiniz.
                </p>
              </div>
            </div>
            <BeforeBiddingPartial
              title="Teminat Bedeli"
              texts={[
                ` Satışların güvenliğini sağlamak ve makinaların satış fiyatında yapılabilecek manipülasyonları engellemek amacıyla, 
                  makina satın almak isteyen bütün kullanıcılardan Teminat Bedeli talep edilir. Tüm kullanıcılardan alınan Teminat Bedeli aynıdır. 
                  Teminat Bedelinin alınmasının amacı kullanıcılara adil bir satış ortamı sunmaktır.`,
                ` Kullanıcılar diledikleri zaman teminat bedelini kesintisiz iade alabilmektedir. İade talebi oluşturulduğu takdirde, 
                  3 iş günü içerisinde teminat bedeliniz kesintisiz iade edilmektedir.`,
              ]}
            />
            <div className="row mb-20">
              <div className="col-15">
                <h2 className="content-header">Olduğu Yerde, Olduğu Şekilde Satış</h2>
              </div>
              <div className="col-15">
                <p>
                  iyiMakina.com’da satışa çıkan iş makinaları, olduğu yerde, olduğu şekilde online olarak satılmaktadır.
                  İş makinasıyla ilgili tüm bilgiler ilan detay sayfasındaki ekspertiz raporlarında yer almaktadır.
                </p>
              </div>
            </div>
            <BeforeBiddingPartial
              title="Teklif Bağlayıcılığı"
              texts={[
                ` Açık Artırma ve Pazaryeri teklifleri alıcının sorumluluğundadır. Yapılan teklifler geri alınamaz. Alıcı, teklifini iptal edilmek isterse, 
                  teminat bedeli iade hakkını kaybedecektir.`,
                ` Açık artırma sonucunda en yüksek teklifi veren sizseniz, makinanın yeni sahibi siz olursunuz. Açık artırmayı kazanan alıcı, 
                  platform kullanım ücretini ödemek zorundadır.`,
              ]}
            />
            <div className="row mb-20">
              <div className="col-15">
                <h2 className="content-header">Alıcılar için Platform Kullanım Ücreti</h2>
              </div>
              <div className="col-15">
                <p>
                  Satılan makinalardan platform kullanım ücreti olarak, alıcı kurum veya kişiden makina satış fiyatının %
                  {turkishNumberWithSuffix(paymentData.platformUsageFeePercentage)}
                  {' '}
                  tutarı talep edilmektedir.
                  Satış fiyatının %
                  {turkishNumberWithSuffix(paymentData.platformUsageFeePercentage)}
                  {' '}
                  tutarının
                  {' '}
                  {maskAmount(paymentConsts.PLATFORM_USAGE_FEE_MINIMUM_EUR, undefined, true)}
                  {' '}
                  Euro’dan az olduğu koşullarda, platform kullanım ücreti
                  {' '}
                  {maskAmount(paymentConsts.PLATFORM_USAGE_FEE_MINIMUM_EUR, undefined, true)}
                  {' '}
                  Euro olarak belirlenecektir.
                  Platform kullanım ücreti ödemeleri Havale veya EFT yoluyla gerçekleştirilmektedir. Platform kullanım ücreti ülke ve koşullara göre
                  farklılık gösterebilmektedir.
                </p>
              </div>
            </div>
            <BeforeBiddingPartial
              title="Ödeme ve Cayma Süresi"
              texts={[
                ` Kazandığınız açık artırmanın bütün ödemesini 5 iş günü içerisinde ödemeniz gerekmektedir. 
                  Ödeme 5 iş gününü geçtiği takdirde gecikme faizi uygulanacaktır. Alıcı satın aldığı iş makinasının ödemesinin tamamını
                  yapana kadar iş makinası askıda kalacaktır. Alıcı açık artırmasını kazandığı iş makinasını satın almaktan vazgeçerse 
                  teminat bedeli iade alma hakkını kaybedecektir.`,
              ]}
            />
            <div className="row mb-20">
              <div className="col-15">
                <h2 className="content-header">Mesafeli Satış Sözleşmesi</h2>
              </div>
              <div className="col-15">
                <p>
                  iyiMakina.com alıcı ve satıcının haklarını mesafeli satış sözleşmesi ile korumaktadır.
                  Buraya tıklayarak mesafeli satış sözleşmesini inceleyebilirsiniz.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

BeforeBidding.propTypes = {
  t: PropTypes.func,
};

export const getServerSideProps = async () => {
  return {
    props: {
      blackHeader: true,
      headTitle: 'Açık Artırma Süreci',
    },
  };
};

BeforeBidding.Layout = Layout;

export default withTranslation('common')(BeforeBidding);
